<script setup>
/**
 * ReportDialog — a member telling us something in a chat is wrong.
 *
 * Reports a single message. A whole conversation cannot be reported (owner
 * decision, 25 Sep 2026: the API refuses it), so `messageId` is required.
 * Deliberately few controls: someone trying to tell us about
 * harassment must not be argued with. The server decides who reviews it
 * (the club's admins for a club chat, the Tap Leagues team for a direct
 * message or when nobody at the club can), and says so above the form.
 */
import {computed, ref, watch} from 'vue';
import {useSafety, REPORT_REASONS, apiMessage} from '~/composables/useSafety';
import {reportAudienceLine} from '~/composables/useSafetyCopy.js';
import {endpoints} from '~/network/endpoints.js';
import {useHttpRequest} from '~/composables/useHttpRequest';

const props = defineProps({
  conversationId: {type: String, required: true},
  messageId: {type: String, required: true},
  /** Shown above the form so the reporter can see what they're reporting. */
  preview: {type: String, default: ''},
});
const emit = defineEmits(['reported']);
const open = defineModel('open', {type: Boolean, default: false});

const {report} = useSafety();
const {httpRequest} = useHttpRequest();

// Who will read it, said before anything is sent (owner finding, 24 Sep 2026).
// The server decides: only it knows whether anyone at the club can review this
// message (nobody reviews their own). Until it answers, and if it cannot, the
// fallback names both possible readers.
const audience = ref(null);
const audienceReady = ref(false); // no line at all rather than one that changes a moment later
const loadAudience = async () => {
  audience.value = null;
  audienceReady.value = false;
  try {
    const query = new URLSearchParams({
      conversationId: props.conversationId,
      conversationMessageId: props.messageId,
    });
    audience.value = await httpRequest({
      ...endpoints.contentReports.audience,
      path: `${endpoints.contentReports.audience.path}?${query}`,
    });
  } catch {
    audience.value = null;
  } finally {
    audienceReady.value = true;
  }
};
const audienceLine = computed(() => reportAudienceLine(audience.value));

const reason = ref('Harassment');
const details = ref('');
const sending = ref(false);
const error = ref(null);
const done = ref(false);
// Set by the server when this was added to the reporter's earlier open report.
const confirmation = ref(null);

watch(open, (isOpen) => {
  if (isOpen) {
    reason.value = 'Harassment';
    details.value = '';
    error.value = null;
    done.value = false;
    confirmation.value = null;
    loadAudience();
  }
});

const submit = async () => {
  sending.value = true;
  error.value = null;
  try {
    const result = await report({
      conversationId: props.conversationId,
      conversationMessageId: props.messageId,
      reason: reason.value,
      details: details.value,
    });
    confirmation.value = result?.confirmationMessage || null;
    done.value = true;
    emit('reported');
  } catch (e) {
    error.value = apiMessage(e, "Couldn't send the report. Please try again.");
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <tl-dialog v-model="open" :title="'Report this message'" :max-width="480">
    <div v-if="done" class="rd-done">
      <v-icon color="success" size="40">mdi-check-circle</v-icon>
      <div class="rd-done-title">{{ confirmation || "Thanks. We'll take a look." }}</div>
      <div class="rd-done-sub">
        A club admin or the Tap Leagues team will review it. You won't hear back unless we need more from you.
        If someone is bothering you, you can also block them from the conversation menu.
      </div>
      <v-btn color="primary" variant="flat" class="mt-4" @click="open = false">Close</v-btn>
    </div>

    <div v-else class="rd-form">
      <div v-if="preview" class="rd-preview">
        <div class="rd-preview-label">You're reporting</div>
        <div class="rd-preview-text">{{ preview }}</div>
      </div>

      <div v-if="audienceReady" class="rd-audience">
        <v-icon size="18" class="rd-audience-icon">mdi-eye-outline</v-icon>
        <span>{{ audienceLine }}</span>
      </div>

      <div class="tl-label">What's wrong?</div>
      <v-radio-group v-model="reason" density="compact" hide-details class="rd-reasons">
        <v-radio v-for="r in REPORT_REASONS" :key="r.value" :label="r.title" :value="r.value"/>
      </v-radio-group>

      <div class="tl-label mt-3">Anything else we should know? <span class="rd-optional">(optional)</span></div>
      <v-textarea
          v-model="details"
          variant="outlined"
          density="comfortable"
          rows="3"
          auto-grow
          counter="2000"
          maxlength="2000"
          placeholder="In your own words"
          hide-details="auto"
      />

      <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-3">{{ error }}</v-alert>

      <div class="rd-actions">
        <v-btn variant="text" :disabled="sending" @click="open = false">Cancel</v-btn>
        <v-btn color="primary" variant="flat" :loading="sending" @click="submit">Send report</v-btn>
      </div>
    </div>
  </tl-dialog>
</template>

<style scoped lang="scss">
.rd-form { display: flex; flex-direction: column; }
.rd-preview {
  background: rgb(var(--v-theme-light));
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
}
.rd-preview-label { font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: rgb(var(--v-theme-textSubtle)); }
.rd-preview-text { font-size: 14px; margin-top: 2px; white-space: pre-wrap; word-break: break-word; max-height: 96px; overflow: auto; }
.rd-audience {
  display: flex; gap: 8px; align-items: flex-start;
  font-size: 13.5px; line-height: 1.4; color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 14px; max-width: 62ch;
}
.rd-audience-icon { margin-top: 1px; flex: none; }
.rd-optional { font-weight: 400; color: rgb(var(--v-theme-textSubtle)); }
.rd-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
.rd-done { text-align: center; padding: 8px 4px; }
.rd-done-title { font-weight: 600; font-size: 16px; margin-top: 8px; }
.rd-done-sub { font-size: 13.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 6px; max-width: 36ch; margin-inline: auto; }
</style>
